import { createAction } from 'redux-actions'
import * as selectors from 'selectors'

import { getCurrentUser, db, TIMESTAMP } from 'utils/firebase'
import { fetchJson } from 'utils/fetch'
import {
  forEach,
  sortBy,
  omitBy,
  pick,
  union,
  groupBy,
  flatten,
  map,
  zipObject,
  keyBy,
  isEqual,
} from 'lodash'

const SET_FUNDS = 'totalbalance/fund/SET_FUNDS'
const RECEIVE_TICKER = 'totalbalance/fund/RECEIVE_TICKER'
const FUNDS_REQUESTED = 'totalbalance/fund/FUNDS_REQUESTED'
const EDIT_FUND = 'totalbalance/fund/EDIT_FUND'
const STOP_EDITING_FUND = 'totalbalance/fund/STOP_EDITING_FUND'
const RECEIVE_HISTORY = 'totalbalance/fund/RECEIVE_HISTORY'
const FETCH_HASH_FUNDS_QUANTITIES =
  'totalbalance/fund/FETCH_HASH_FUNDS_QUANTITIES'
const SET_ALL_ACCOUNTS = 'totalbalance/fund/SET_ALL_ACCOUNTS'
const DELETE_FUND = 'totalbalance/fund/DELETE_FUND'

const initialState = {
  funds: {},
  ticker: {},
  fundsAreLoading: true,
  history: {},
}

export default function(state = initialState, action) {
  switch (action.type) {
    case RECEIVE_TICKER:
      return {
        ...state,
        ticker: action.payload,
      }
    case SET_FUNDS:
      return {
        ...state,
        fundsAreLoading: false,
        funds: {
          ...action.payload,
        },
      }
    case FUNDS_REQUESTED:
      return {
        ...state,
        fundsAreLoading: true,
      }
    case EDIT_FUND:
      return {
        ...state,
        funds: {
          ...state.funds,
          [action.payload._uid]: {
            ...action.payload,
            _edit: true,
          },
        },
      }
    case STOP_EDITING_FUND:
      return {
        ...state,
        funds: {
          ...state.funds,
          [action.payload._uid]: {
            ...action.payload,
            _edit: false,
          },
        },
      }
    case RECEIVE_HISTORY:
      return {
        ...state,
        history: action.payload,
      }
    case SET_ALL_ACCOUNTS:
      return {
        ...state,
        accounts: action.payload,
      }
    default:
      return state
  }
}

export const setFunds = createAction(SET_FUNDS)

export const saveFundAndRefreshBalance = fund => dispatch =>
  dispatch(saveFund(fund)).then(() => dispatch(fetchHashFundsQuantities()))

const saveFund = fund => dispatch =>
  getCurrentUser().then(user => {
    dispatch({ type: 'saveFund', payload: fund })
    var ref
    if (fund._uid) {
      ref = db.ref(`funds/${user.uid}/${fund._uid}`)
    } else {
      ref = db.ref(`funds/${user.uid}`).push()
    }
    // firebase doesn't like undefined and prefer null
    let fundToSave = { ...fund }
    forEach(fundToSave, (value, key) => {
      if (value === undefined) {
        fundToSave[key] = null
      }
    })
    // removes fields starting with _
    fundToSave = omitBy(fundToSave, (value, key) => key[0] === '_')
    ref.set({
      ...fundToSave,
      createdAt: fundToSave.createdAt || TIMESTAMP,
    })
  })

export const deleteFund = payload => dispatch => {
  dispatch({ type: DELETE_FUND, payload })
  getCurrentUser().then(user =>
    db.ref(`funds/${user.uid}/${payload._uid}`).remove()
  )
}

export const loadAllAccounts = () => dispatch =>
  db
    .ref(`funds`)
    .once('value')
    .then(snapshot => dispatch(setAllAccounts(snapshot.val())))

const setAllAccounts = createAction(SET_ALL_ACCOUNTS)

export const setFundsSnapshot = ({ snapshot, userId }) => dispatch => {
  const data = {}
  snapshot.forEach(s => {
    data[s.key] = {
      ...s.val(),
      _uid: s.key,
      _userId: userId,
    }
  })
  dispatch(setFunds(data))
}

export const fetchHistories = () => (dispatch, getState) => {
  dispatch({ type: 'REQUEST_HISTORIES' })
  const currencies = union(selectors.funds(getState()).map(d => d.currency))
  return Promise.all(
    currencies.map(currency =>
      db
        .ref(`history/${currency}`)
        .once('value')
        .then(snapshot => snapshot.val())
    )
  )
    .then(histories =>
      histories.map((h, i) => ({
        ...h,
        currency: currencies[i],
      }))
    )
    .then(histories => histories.filter(h => !!h.price))
    .then(histories =>
      dispatch(
        receiveHistory(
          map(
            groupBy(
              flatten(
                histories.map((h, i) =>
                  h.price.map(([ts, val]) => ({
                    currency: h.currency,
                    ts,
                    val,
                  }))
                )
              ),
              d => Math.round(d.ts / 86400000)
            ),
            (values, timestampRounded) => ({
              timestamp: timestampRounded * 86400000,
              // {btc: 100, ltc: 2}
              ...zipObject(values.map(v => v.currency), values.map(v => v.val)),
            })
          )
        )
      )
    )
}

const receiveHistory = createAction(RECEIVE_HISTORY)

export const fetchHashFundsQuantities = () => (dispatch, getState) => {
  dispatch({ type: FETCH_HASH_FUNDS_QUANTITIES })
  const hashFunds = selectors.hashFunds(getState())
  return Promise.all(
    [
      {
        currency: 'ETH',
        retreive: hashes =>
          Promise.all([
            fetchJson(`https://api.etherscan.io/api?module=
account&action=balancemulti&address=${hashes.join(',')}&tag=latest`).then(
              ({ result }) => result
            ),
            Promise.all(
              hashes.map(hash =>
                fetchJson(`https://api.etherscan.io/api?module=account&action=txlist&address=${hash}
&startblock=0&endblock=99999999&sort=asc`).then(({ result }) => ({
                  hash,
                  history: result,
                }))
              )
            ),
            Promise.all(
              hashes.map(hash =>
                fetchJson(`https://api.etherscan.io/api?module=account&action=
txlistinternal&address=${hash}&startblock=0&endblock=2702578&sort=asc`).then(
                  ({ result }) => ({
                    hash,
                    history: result,
                  })
                )
              )
            ),
            Promise.all(
              hashes.map(hash =>
                fetchJson(
                  `https://api.ethplorer.io/getAddressInfo/${hash}?apiKey=freekey`
                ).then(result => ({
                  hash: result.address,
                  tokens: result.tokens.map(d => {
                    d.balance = d.balance / Math.pow(10, d.tokenInfo.decimals)
                    delete d.tokenInfo.price
                    return d
                  }),
                }))
              )
            ),
          ]).then(([balances, txs, internals, tokens]) =>
            balances.map(({ account, balance }) => ({
              hash: account,
              balance: parseFloat(balance) / Math.pow(10, 18),
              tokens: [].concat(
                ...tokens.filter(d => d.hash === account).map(d => d.tokens)
              ),
              history: sortBy(
                [
                  ...[].concat(
                    ...txs.filter(d => d.hash === account).map(d => d.history)
                  ),
                  ...[].concat(
                    ...internals
                      .filter(d => d.hash === account)
                      .map(d => d.history)
                  ),
                ].map(({ confirmations, ...h }) => ({
                  ...h,
                  value: parseInt(h.value, 10) / Math.pow(10, 18),
                  timestamp: parseInt(h.timeStamp, 10) * 1000,
                })),
                ['timestamp']
              ),
            }))
          ),
      },
      {
        currency: 'BTC',
        retreive: hashes =>
          fetchJson(
            `https://blockchain.info/balance?active=${hashes.join(
              '|'
            )}&cors=true`
          ).then(data =>
            Object.keys(data).map(key => ({
              hash: key,
              balance: parseFloat(data[key].final_balance) / 100000000,
            }))
          ),
      },
    ].map(({ currency, retreive }) => {
      const funds = hashFunds.filter(f => f.currency === currency)
      const hashes = [].concat(...funds.map(f => f.hashes)).filter(c => !!c)
      if (hashes.length) {
        return retreive(hashes).then(hashAndBalance => {
          hashAndBalance = keyBy(hashAndBalance, 'hash')
          return Promise.all(
            funds.map(fund => {
              const relatedHashAndBalance = Object.values(
                pick(hashAndBalance, fund.hashes)
              )
              if (relatedHashAndBalance.length !== fund.hashes.length) {
                console.error(
                  `a data is missing for the fund ${fund._uid}`,
                  fund
                )
              }
              const balance = relatedHashAndBalance.reduce(
                (sum, value) => sum + value.balance,
                0
              )
              const flatten = key =>
                []
                  .concat(...relatedHashAndBalance.map(d => d[key]))
                  .filter(d => !!d)
              const history = flatten('history')
              const tokens = flatten('tokens')
              if (
                balance !== fund.balance ||
                (history.length > 0 && !isEqual(history, fund.history)) ||
                (tokens.length > 0 && !isEqual(tokens, fund.tokens))
              ) {
                return dispatch(
                  saveFund({
                    ...fund,
                    balance,
                    history,
                    tokens,
                  })
                )
              }
              return null
            })
          )
        })
      } else {
        return Promise.resolve()
      }
    })
  )
}

export const fetchTicker = () => dispatch => {
  db.ref('currencies').on('value', snapshot => {
    const { btc, fiat } = snapshot.val()
    return dispatch(
      receiveTicker({
        btc: JSON.parse(btc.data),
        fiat: JSON.parse(fiat.data),
      })
    )
  })
}

export const editFund = createAction(EDIT_FUND)
export const stopEditingFund = createAction(STOP_EDITING_FUND)

export const receiveTicker = createAction(RECEIVE_TICKER)



// WEBPACK FOOTER //
// ./src/actions/fund.js